import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { minsToHhMm } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyShuruUsers } from './companyShuruApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable users table for the Shuru section.
 *
 * Volume only — chats, messages, time and when they last talked. Nothing a
 * person said to Shuru, and nothing about how they felt, appears here or on the
 * profile behind it.
 *
 * Every user of the population is listed, including those who have never opened
 * Shuru — they show dashes rather than zeros, so "hasn't started" reads
 * differently from "talked once and stopped".
 */
const ShuruUsersTable = ({
  fetcher = fetchCompanyShuruUsers,
  onOpenUser,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const talked = row.messages > 0;
          return {
            ...row,
            chatsText: talked ? row.chats.toLocaleString() : '-',
            messagesText: talked ? row.messages.toLocaleString() : '-',
            timeText: talked ? minsToHhMm(row.totalTimeMins) : '-',
            lastChatDate: row.lastChatDate || '-'
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('ShuruUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Chats', key: 'chatsText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Messages', key: 'messagesText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Time', key: 'timeText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Last Chat', key: 'lastChatDate', sortable: false, align: 'center', type: 'text3' },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

ShuruUsersTable.propTypes = {
  fetcher: PropTypes.func,
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default ShuruUsersTable;
